/**
 * Custom hooks for Containers Recap API
 *
 * Provides hooks for:
 * - Map positions (container coordinates for map display)
 * - Stats recap (risk, status, document distributions)
 * - Table data (paginated container list)
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type {
  ContainerMapResponse,
  StatsRecapResponse,
  ContainerTableResponse,
  ContainerTableQueryParams,
  RiskLevel,
  PhysicalStatusGroup,
  ShipmentStatusFilter,
} from "@/types/api/containers-recap";

// ============================================================================
// QUERY KEYS
// ============================================================================

export interface StatsQueryParams {
  start_date?: string;
  end_date?: string;
  risk_level?: string;
  physical_status?: string;
  shipment_status?: string;
  carrier_scac?: string;
  carrier_name?: string;
  has_doc_issues?: boolean;
  has_active_alerts?: boolean;
}

export const CONTAINERS_RECAP_QUERY_KEYS = {
  all: ["containers-recap"] as const,
  map: (startDate: string | null, endDate: string | null) =>
    [...CONTAINERS_RECAP_QUERY_KEYS.all, "map", startDate, endDate] as const,
  stats: (params: StatsQueryParams) =>
    [...CONTAINERS_RECAP_QUERY_KEYS.all, "stats", params] as const,
  table: (params: ContainerTableQueryParams) =>
    [...CONTAINERS_RECAP_QUERY_KEYS.all, "table", params] as const,
  carriersInUse: (startDate: string | null, endDate: string | null) =>
    [...CONTAINERS_RECAP_QUERY_KEYS.all, "carriers-in-use", startDate, endDate] as const,
};

// ============================================================================
// MAP POSITIONS HOOK
// ============================================================================

/**
 * Hook to fetch container positions for map display
 *
 * @param startDate - Start of date range (ISO format: "2024-01-01") - filters by ETA/ETD
 * @param endDate - End of date range (ISO format: "2024-01-31") - filters by ETA/ETD
 * @param enabled - Whether the query should be enabled (default: true)
 *
 * Returns containers with:
 * - Latest coordinates from tracking events
 * - Route info (origin/destination port names)
 * - Risk level and status
 */
export function useContainerMapPositions(
  startDate: string | null,
  endDate: string | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: CONTAINERS_RECAP_QUERY_KEYS.map(startDate, endDate),
    queryFn: async (): Promise<ContainerMapResponse> => {
      return await apiGet<ContainerMapResponse>("/containers-recap/map", {
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
        },
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    placeholderData: (previousData) => previousData, // Keep showing previous data during refetch
  });
}

// ============================================================================
// STATS RECAP HOOK
// ============================================================================

export interface UseContainerStatsOptions {
  startDate: string | null;
  endDate: string | null;
  riskLevels?: RiskLevel[];
  physicalStatus?: PhysicalStatusGroup[];
  shipmentStatus?: ShipmentStatusFilter[];
  carrierScac?: string;
  carrierName?: string;
  hasDocIssues?: boolean;
  hasActiveAlerts?: boolean;
  enabled?: boolean;
}

/**
 * Hook to fetch aggregated container statistics
 *
 * Accepts the same filters as the table endpoint to ensure coherent data.
 *
 * Returns distributions for:
 * - Risk levels (Low/Medium/High/Critical)
 * - Container status
 * - Document status (Complete/Pending/Error)
 */
export function useContainerStatsRecap(options: UseContainerStatsOptions) {
  const {
    startDate,
    endDate,
    riskLevels,
    physicalStatus,
    shipmentStatus,
    carrierScac,
    carrierName,
    hasDocIssues,
    hasActiveAlerts,
    enabled = true,
  } = options;

  const params: StatsQueryParams = {
    ...(startDate && { start_date: startDate }),
    ...(endDate && { end_date: endDate }),
    ...(riskLevels && riskLevels.length > 0 && { risk_level: riskLevels.join(",") }),
    ...(physicalStatus && physicalStatus.length > 0 && { physical_status: physicalStatus.join(",") }),
    ...(shipmentStatus && shipmentStatus.length > 0 && { shipment_status: shipmentStatus.join(",") }),
    ...(carrierScac && { carrier_scac: carrierScac }),
    ...(carrierName && { carrier_name: carrierName }),
    ...(hasDocIssues === true && { has_doc_issues: true }),
    ...(hasActiveAlerts === true && { has_active_alerts: true }),
  };

    return useQuery({
    queryKey: CONTAINERS_RECAP_QUERY_KEYS.stats(params),
    queryFn: async (): Promise<StatsRecapResponse> => {
      return await apiGet<StatsRecapResponse>("/containers-recap/stats", {
        params: params as Record<string, string | number | boolean | null | undefined>,
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    placeholderData: (previousData) => previousData, // Keep showing previous data during refetch
  });
}

// ============================================================================
// TABLE DATA HOOK
// ============================================================================

export interface UseContainerTableOptions {
  startDate: string | null;
  endDate: string | null;
  skip?: number;
  limit?: number;
  /** Include sea route coordinates per container (for map display) */
  includeSeaRoute?: boolean;
  search?: string;
  status?: string;
  riskLevels?: RiskLevel[];
  docStatus?: string;
  physicalStatus?: PhysicalStatusGroup[];
  shipmentStatus?: ShipmentStatusFilter[];
  carrierScac?: string;
  carrierName?: string;
  hasDocIssues?: boolean;
  hasActiveAlerts?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
}

/**
 * Hook to fetch paginated container table data
 *
 * @param options - Query options including filters, sorting, and pagination
 *
 * Filters:
 * - startDate/endDate: Date range (ISO format: "2024-01-01") - filters by ETA/ETD
 * - status: Legacy status filter
 * - riskLevels: Array of risk levels (supports multi-select)
 * - docStatus: Document status filter ("pending" or "complete")
 * - physicalStatus: Array of physical status groups
 * - carrierScac: Filter by carrier SCAC code
 * - hasDocIssues: Boolean filter for containers with document issues
 *
 * Sorting:
 * - sortBy: "eta" | "risk" | "progress" | "doc_issues" | "issues" | "last_update"
 * - sortOrder: "asc" | "desc"
 *
 * Returns containers with:
 * - Route (origin/destination port names)
 * - Carrier name
 * - Progress percentage (calculated from position/dates)
 * - ETA and status
 * - Document info
 * - Last update tracking
 * - Physical status
 */
export function useContainerTable(options: UseContainerTableOptions) {
  const {
    startDate,
    endDate,
    skip = 0,
    limit = 50,
    includeSeaRoute,
    search,
    status,
    riskLevels,
    docStatus,
    physicalStatus,
    shipmentStatus,
    carrierScac,
    carrierName,
    hasDocIssues,
    hasActiveAlerts,
    sortBy,
    sortOrder,
    enabled = true,
  } = options;

  const params: ContainerTableQueryParams = {
    ...(startDate && { start_date: startDate }),
    ...(endDate && { end_date: endDate }),
    skip,
    limit,
    ...(includeSeaRoute === true && { include_sea_route: true }),
    ...(search && { search }),
    ...(status && { status }),
    ...(riskLevels && riskLevels.length > 0 && { risk_level: riskLevels.join(",") }),
    ...(docStatus && { doc_status: docStatus }),
    ...(physicalStatus && physicalStatus.length > 0 && { physical_status: physicalStatus.join(",") }),
    ...(shipmentStatus && shipmentStatus.length > 0 && { shipment_status: shipmentStatus.join(",") }),
    ...(carrierScac && { carrier_scac: carrierScac }),
    ...(carrierName && { carrier_name: carrierName }),
    ...(hasDocIssues === true && { has_doc_issues: true }),
    ...(hasActiveAlerts === true && { has_active_alerts: true }),
    ...(sortBy && { sort_by: sortBy }),
    ...(sortOrder && { sort_order: sortOrder }),
  };

  return useQuery({
    queryKey: CONTAINERS_RECAP_QUERY_KEYS.table(params),
    queryFn: async (): Promise<ContainerTableResponse> => {
      return await apiGet<ContainerTableResponse>("/containers-recap/table", {
        params: { ...params },
      });
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    placeholderData: (previousData) => previousData, // Keep showing previous data during refetch
  });
}

// ============================================================================
// REFETCH HOOKS
// ============================================================================

/**
 * Hook to manually refetch all containers recap data
 */
export function useRefetchContainersRecap() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
  };
}

/**
 * Hook to manually refetch map positions
 */
export function useRefetchMapPositions() {
  const queryClient = useQueryClient();

  return (startDate: string | null, endDate: string | null) => {
    queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.map(startDate, endDate) });
  };
}

/**
 * Hook to manually refetch stats
 */
export function useRefetchStats() {
  const queryClient = useQueryClient();

  return (startDate: string | null, endDate: string | null) => {
    queryClient.invalidateQueries({
      queryKey: CONTAINERS_RECAP_QUERY_KEYS.stats({
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      }),
    });
  };
}

/**
 * Hook to manually refetch table data
 */
export function useRefetchTable() {
  const queryClient = useQueryClient();

  return (params: ContainerTableQueryParams) => {
    queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.table(params) });
  };
}

// ============================================================================
// CARRIERS IN USE HOOK
// ============================================================================

interface CarrierInUse {
  carrier_name: string;
  count: number;
}

interface CarriersInUseResponse {
  carriers: CarrierInUse[];
}

/**
 * Hook to fetch distinct carrier names actually used in containers.
 */
export function useCarriersInUse(
  startDate: string | null,
  endDate: string | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: CONTAINERS_RECAP_QUERY_KEYS.carriersInUse(startDate, endDate),
    queryFn: async (): Promise<CarriersInUseResponse> => {
      return await apiGet<CarriersInUseResponse>("/containers-recap/carriers-in-use", {
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
        },
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// SINGLE SHIPMENT/CONTAINER HOOK
// ============================================================================

/**
 * Response type for single shipment endpoint
 */
export interface ShipmentResponse {
  container_id: string;
  container_number: string;
  company_id: string;

  // Parties
  shipper_company_id: string | null;
  consignee_company_id: string | null;
  freight_forwarder_id: string | null;
  notify_party_company_id: string | null;

  // Booking & References
  booking_id: string | null;
  booking_number: string | null;
  bl_number: string | null;
  service_contract_number: string | null;
  purchase_order_number: string | null;
  sequence_in_booking: number;

  // Route
  origin_country_code: string | null;
  destination_country_code: string | null;
  origin_port_id: string | null;
  destination_port_id: string | null;
  origin_port_name: string | null;
  destination_port_name: string | null;
  origin_port_lat: number | null;
  origin_port_lon: number | null;
  destination_port_lat: number | null;
  destination_port_lon: number | null;

  // Transport
  cargo_type: string;
  transport_mode: string;
  container_type: string | null;
  container_size: string | null;
  incoterms: string | null;
  vessel_name: string | null;
  voyage_number: string | null;
  carrier_name: string | null;

  // Financial
  total_value_usd: number | null;
  currency: string;
  freight_charges_usd: number | null;
  insurance_value_usd: number | null;

  // Weight & Volume
  total_weight_kg: number | null;
  total_volume_cbm: number | null;
  total_packages: number | null;

  // Cargo
  cargo_description: string | null;
  hs_code_primary: string | null;
  is_hazardous: boolean;
  hazard_class: string | null;

  // Status
  status: string;
  status_changed_at: string | null;
  physical_status: string;
  physical_status_changed_at: string | null;
  current_leg_number: number;
  risk_score: number | null;
  progress_percentage: number | null;

  // Documents
  documents_complete: boolean;
  documents_validated: boolean;
  all_validations_passed: boolean;

  // Timeline dates
  booking_date: string | null;
  estimated_departure_date: string | null;
  actual_departure_date: string | null;
  estimated_arrival_date: string | null;
  actual_arrival_date: string | null;
  estimated_delivery_date: string | null;

  // Temperature control
  requires_temperature_control: boolean;
  temperature_setpoint_celsius: number | null;

  // Tracking
  last_tracked_at: string | null;
  tracking_provider: string | null;
  last_email_update_at: string | null;
  /** When container was created from imported email (link to Trade Communications) */
  source_email_message_id: string | null;
  alert_count: number;
  has_active_alerts: boolean;

  // Notes
  internal_notes: string | null;
  customer_notes: string | null;
  special_instructions: string | null;

  // Metadata
  extra_metadata: Record<string, unknown> | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

/**
 * Hook to fetch a single shipment/container by ID
 *
 * Uses the /shipments/{id} endpoint
 *
 * @param id - Container/Shipment UUID
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useShipmentById(id: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ["shipment", id],
    queryFn: async () => {
      if (!id) throw new Error("No shipment ID provided");
      return await apiGet<ShipmentResponse>(`/shipments/${id}`);
    },
    enabled: enabled && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
